import { validOwnedFullGameRef } from '@runtime/core/user/ownedFullGames'
import { normalizeGameResultReceipts } from '@runtime/core/user/gameResultReceipts'
import { normalizeGameResultsOutbox } from '@runtime/core/user/gameResultsOutbox'
import { SUPPORTED_LOCALES } from '@runtime/core/i18n/localeConfig'
import { uid } from '@runtime/shared/utils/id'
import {
  computeLevelFromRating,
  type GameHistoryEntry,
  type UserGamesSummary,
  type UserProfile,
} from '@runtime/core/user/userModel'
import { normalizeUserRole } from '@runtime/core/user/userRole'
import type {
  AvatarColor,
  StrategyHelpMode,
} from '@runtime/core/store/appStoreTypes'
import { getGuestNick } from '@runtime/core/store/storeText'
import { readStorageText } from '@runtime/shared/utils/storage'

type StoredState = Record<string, unknown>

export const STORE_KEY = 'us_app_store_v5'

export const DEFAULT_THEME_ID = 'united-square-original'
export const LEGACY_STRATEGY_HELP_KEY = 'us_help_strategy'
export const LEGACY_POSTER_UNLOCK_ALL_KEY = 'us_poster_private_unlock_all'
export const LEGACY_POSTER_UNLOCK_IDS_KEY = 'us_poster_private_unlock_ids'

const LOCALES = new Set<string>(SUPPORTED_LOCALES)
const AVATAR_COLORS: AvatarColor[] = ['RED', 'BLU', 'YEL', 'GRN']

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object'
}

function readFiniteNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function normalizeOptionalString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined
}

function normalizeGameHistoryEntry(raw: unknown): GameHistoryEntry | null {
  if (!isRecord(raw)) return null

  const id = typeof raw.id === 'string' && raw.id ? raw.id : uid()
  const ts = readFiniteNumber(raw.ts) ?? Date.now()
  const result =
    raw.result === 'win' || raw.result === 'loss' || raw.result === 'draw'
      ? raw.result
      : undefined
  const opponents = Array.isArray(raw.opponents)
    ? raw.opponents.filter(
        (entry): entry is string => typeof entry === 'string',
      )
    : undefined

  return {
    id,
    ts,
    kind: normalizeOptionalString(raw.kind),
    grid: readFiniteNumber(raw.grid) ?? undefined,
    vsAI: typeof raw.vsAI === 'boolean' ? raw.vsAI : undefined,
    myName: normalizeOptionalString(raw.myName),
    opponents,
    result,
    scoreFor: readFiniteNumber(raw.scoreFor),
    scoreAgainst: readFiniteNumber(raw.scoreAgainst),
    durationMs: readFiniteNumber(raw.durationMs) ?? undefined,
    ...(validOwnedFullGameRef(raw.fullGameRef)
      ? { fullGameRef: raw.fullGameRef }
      : {}),
  }
}

function normalizeUserGamesSummary(raw: unknown): UserGamesSummary | undefined {
  if (!isRecord(raw)) return undefined

  const asCounters = (value: unknown) => {
    if (!isRecord(value)) return undefined
    const played = readFiniteNumber(value.played)
    if (played == null) return undefined
    return {
      played,
      wins: readFiniteNumber(value.wins) ?? 0,
      losses: readFiniteNumber(value.losses) ?? 0,
      draws: readFiniteNumber(value.draws) ?? 0,
    }
  }

  const asPuzzle = (value: unknown) => {
    if (!isRecord(value)) return undefined
    const played = readFiniteNumber(value.played)
    const completed = readFiniteNumber(value.completed)
    if (played == null || completed == null) return undefined
    return { played, completed }
  }

  return {
    total: readFiniteNumber(raw.total) ?? undefined,
    vsAI: asCounters(raw.vsAI),
    vsPlayers: asCounters(raw.vsPlayers),
    puzzle: asPuzzle(raw.puzzle),
  }
}

function safeJsonParse<T>(raw: string | null): T | null {
  if (!raw) return null
  try {
    return JSON.parse(raw) as T
  } catch {
    return null
  }
}

function readString(key: string): string | null {
  const value = readStorageText(key)
  return value != null ? String(value) : null
}

function readPersistedStoreState() {
  const raw = readString(STORE_KEY)
  const parsed = safeJsonParse<StoredState>(raw)
  if (!parsed) return null
  if (
    parsed &&
    typeof parsed === 'object' &&
    parsed.state &&
    typeof parsed.state === 'object'
  ) {
    return parsed.state as StoredState
  }
  if (parsed && typeof parsed === 'object') return parsed
  return null
}

export function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n))
}

export function normalizeLocale(raw: unknown) {
  const s = String(raw || '')
    .trim()
    .toLowerCase()
  if (LOCALES.has(s)) return s
  const short = s.slice(0, 2)
  return LOCALES.has(short) ? short : 'en'
}

export function normalizeAvatarColor(raw: unknown): AvatarColor {
  const s = String(raw || '')
    .trim()
    .toUpperCase()
  return (AVATAR_COLORS as string[]).includes(s) ? (s as AvatarColor) : 'BLU'
}

export function normalizeStrategyHelpMode(raw: unknown): StrategyHelpMode {
  const s = String(raw || '')
    .trim()
    .toLowerCase()
  return s === 'on' || s === 'off' ? s : 'ask'
}

function normalizeStringList(raw: unknown): string[] {
  if (!Array.isArray(raw)) return []

  const seen = new Set<string>()
  for (const value of raw) {
    const next = String(value || '').trim()
    if (!next) continue
    seen.add(next)
  }
  return Array.from(seen)
}

export function randomGuestNick(locale: unknown = pickInitialLocale()) {
  return getGuestNick((Math.random() * 1000) | 0, locale)
}

export function normalizeThemeTokens(raw: unknown): string[] | null {
  if (!Array.isArray(raw) || raw.length < 4) return null

  const out: string[] = []
  for (let i = 0; i < 4; i++) {
    const v = String(raw[i] || '').trim()
    const m = /^#?([a-fA-F0-9]{6})$/.exec(v)
    if (!m) return null
    out.push('#' + m[1].toUpperCase())
  }

  return out
}

export function normalizeLoadedUser(raw: unknown): UserProfile | null {
  if (!isRecord(raw)) return null

  const ratingValue = readFiniteNumber(raw.rating)
  const rating = ratingValue && ratingValue > 0 ? ratingValue : 1200
  const createdAtValue = readFiniteNumber(raw.createdAt)
  const history = Array.isArray(raw.history)
    ? raw.history
        .map((entry) => normalizeGameHistoryEntry(entry))
        .filter((entry): entry is GameHistoryEntry => entry != null)
    : undefined
  const favorites = Array.isArray(raw.favorites)
    ? raw.favorites.filter(
        (entry): entry is string => typeof entry === 'string',
      )
    : undefined

  return {
    id: typeof raw.id === 'string' && raw.id ? raw.id : uid(),
    createdAt:
      createdAtValue && createdAtValue > 0 ? createdAtValue : Date.now(),
    pseudo: normalizeOptionalString(raw.pseudo),
    email: normalizeOptionalString(raw.email),
    role: normalizeUserRole(raw.role),
    avatarDataUrl:
      raw.avatarDataUrl == null
        ? null
        : normalizeOptionalString(raw.avatarDataUrl),
    rating,
    level: computeLevelFromRating(rating),
    games: normalizeUserGamesSummary(raw.games),
    history,
    favorites,
  }
}

export function pickInitialThemeId() {
  const persisted = readPersistedStoreState()
  if (persisted && persisted.themeId) return String(persisted.themeId)
  return DEFAULT_THEME_ID
}

export function pickInitialThemeTokens() {
  const persisted = readPersistedStoreState()
  if (persisted && persisted.themeTokens) {
    const norm = normalizeThemeTokens(persisted.themeTokens)
    if (norm) return norm
  }
  return null
}

export function pickInitialLocale() {
  const persisted = readPersistedStoreState()
  if (persisted && persisted.locale) return normalizeLocale(persisted.locale)

  try {
    const nav = (typeof navigator !== 'undefined' && navigator.language) || 'en'
    return normalizeLocale(nav)
  } catch {
    return 'en'
  }
}

export function pickInitialStrategyHelpMode(): StrategyHelpMode {
  const persisted = readPersistedStoreState()
  if (persisted && persisted.strategyHelpMode != null) {
    return normalizeStrategyHelpMode(persisted.strategyHelpMode)
  }

  const raw = readString(LEGACY_STRATEGY_HELP_KEY)
  if (raw == null) return 'ask'
  return normalizeStrategyHelpMode(safeJsonParse<unknown>(raw) ?? raw)
}

export function pickInitialNick() {
  const persisted = readPersistedStoreState()
  if (persisted && persisted.onlineNick) return String(persisted.onlineNick)

  return randomGuestNick()
}

export function pickInitialAvatarColor(): AvatarColor {
  const persisted = readPersistedStoreState()
  if (persisted && persisted.onlineAvatarColor) {
    return normalizeAvatarColor(persisted.onlineAvatarColor)
  }

  return 'BLU'
}

export function pickInitialArcadeIntroSeen() {
  const persisted = readPersistedStoreState()
  if (persisted && typeof persisted.arcadeIntroSeen === 'boolean') {
    return persisted.arcadeIntroSeen
  }
  return false
}

export function pickInitialArcadeSfxEnabled() {
  const persisted = readPersistedStoreState()
  if (persisted && typeof persisted.arcadeSfxEnabled === 'boolean') {
    return persisted.arcadeSfxEnabled
  }
  return true
}

export function pickInitialArcadeVibrationEnabled() {
  const persisted = readPersistedStoreState()
  if (persisted && typeof persisted.arcadeVibrationEnabled === 'boolean') {
    return persisted.arcadeVibrationEnabled
  }
  return true
}

export function pickInitialArcadeEnergyStartLevel() {
  const persisted = readPersistedStoreState()
  if (persisted) {
    const n = Number(persisted.arcadeEnergyStartLevel)
    if (Number.isFinite(n)) return Math.max(1, Math.floor(n))
  }
  return 1
}

export function pickInitialArcadeFusionObstacles() {
  const persisted = readPersistedStoreState()
  if (persisted && typeof persisted.arcadeFusionObstacles === 'boolean') {
    return persisted.arcadeFusionObstacles
  }
  return true
}

export function pickInitialArcadeFusionTimeMin() {
  const persisted = readPersistedStoreState()
  if (persisted) {
    const n = Number(persisted.arcadeFusionTimeMin)
    if (Number.isFinite(n)) return Math.max(1, Math.min(30, Math.floor(n)))
  }
  return 5
}

export function pickInitialPosterPrivateUnlockedAll() {
  const persisted = readPersistedStoreState()
  if (persisted && typeof persisted.posterPrivateUnlockedAll === 'boolean') {
    return persisted.posterPrivateUnlockedAll
  }
  return readString(LEGACY_POSTER_UNLOCK_ALL_KEY) === '1'
}

export function pickInitialPosterPrivateUnlockedIds() {
  const persisted = readPersistedStoreState()
  if (persisted && persisted.posterPrivateUnlockedIds != null) {
    return normalizeStringList(persisted.posterPrivateUnlockedIds)
  }

  const raw = readString(LEGACY_POSTER_UNLOCK_IDS_KEY)
  if (!raw) return []
  return normalizeStringList(safeJsonParse<unknown>(raw))
}

export function pickInitialAuthToken() {
  const persisted = readPersistedStoreState()
  if (persisted && persisted.authToken) return String(persisted.authToken)
  return null
}

export function pickInitialUser() {
  const persisted = readPersistedStoreState()
  const token = persisted && persisted.authToken
  if (!token) return null

  if (persisted && persisted.user) {
    const norm = normalizeLoadedUser(persisted.user)
    if (norm) return norm
  }

  return null
}

export function pickInitialGameResultsOutbox() {
  const persisted = readPersistedStoreState()
  return normalizeGameResultsOutbox(
    persisted?.gameResultsOutbox,
    pickInitialUser(),
  )
}

export function pickInitialGameResultReceipts() {
  return normalizeGameResultReceipts(
    readPersistedStoreState()?.gameResultReceipts,
  )
}
